// IDE-inspired dark theme
const theme = {
  colors: {
    background: '#0d1117',
    backgroundSecondary: '#161b22',
    backgroundTertiary: '#21262d',
    border: '#30363d',
    text: '#c9d1d9',
    textSecondary: '#7d8590',
    textBright: '#f0f6fc',
    primary: '#58a6ff',
    primaryHover: '#1a5fcb',
    success: '#238636',
    successHover: '#2ea043',
    danger: '#da3633',
    dangerHover: '#f85149',
    warning: '#f85149',
    purple: '#6f42c1',
    blue: '#1f6feb',
  },
  fonts: {
    mono: 'Monaco, "Cascadia Code", Consolas, monospace',
    sans: 'system-ui, -apple-system, sans-serif',
  },
};

// Reusable style objects
export const styles = {
  body: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontFamily: theme.fonts.mono,
    minHeight: '100vh',
    margin: 0,
    padding: '20px'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: `1px solid ${theme.colors.border}`
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    margin: 0
  },
  button: {
    backgroundColor: theme.colors.success,
    color: '#ffffff',
    border: 'none',
    padding: '12px 16px',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    backgroundColor: theme.colors.backgroundTertiary,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text
  },
  buttonDanger: {
    backgroundColor: theme.colors.danger,
  },
  input: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  dateInput: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    colorScheme: 'dark'
  },
  card: {
    backgroundColor: theme.colors.backgroundSecondary,
    padding: '20px',
    marginBottom: '16px',
    borderRadius: '8px',
    border: `1px solid ${theme.colors.border}`
  },
  form: {
    display: 'grid',
    gap: '16px',
    maxWidth: '600px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    color: theme.colors.textBright,
    fontSize: '14px',
    fontWeight: '500'
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '30px'
  },
  statCard: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: '12px',
    color: theme.colors.textSecondary
  },
  tab: {
    padding: '12px 20px',
    backgroundColor: theme.colors.backgroundTertiary,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    cursor: 'pointer',
    borderRadius: '6px 6px 0 0',
    marginRight: '4px',
    fontSize: '14px'
  },
  tabActive: {
    backgroundColor: theme.colors.primary,
    color: '#ffffff',
    borderColor: theme.colors.primary
  },
  emailCard: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px'
  },
  emailPreview: {
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '16px',
    fontSize: '13px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    maxHeight: '300px',
    overflow: 'auto'
  },
  revenueCard: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px'
  },
  revenueBar: {
    height: '8px',
    borderRadius: '4px',
    backgroundColor: theme.colors.backgroundTertiary,
    overflow: 'hidden',
    marginTop: '8px'
  },
  revenueBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90%',
    overflow: 'auto'
  },
  emailEditor: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '16px',
    fontSize: '14px',
    fontFamily: theme.fonts.mono,
    resize: 'vertical',
    minHeight: '300px',
    width: '100%',
    outline: 'none',
    lineHeight: '1.5'
  },
  // Table styles for contracts
  tableContainer: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden'
  },
  tableHeader: {
    backgroundColor: theme.colors.backgroundTertiary,
    padding: '16px',
    borderBottom: `1px solid ${theme.colors.border}`
  },
  tableFilters: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px'
  },
  searchInput: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '14px',
    minWidth: '250px',
    outline: 'none'
  },
  select: {
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '14px',
    outline: 'none'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeaderCell: {
    backgroundColor: theme.colors.backgroundTertiary,
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: theme.colors.textBright,
    borderBottom: `1px solid ${theme.colors.border}`,
    fontSize: '14px'
  },
  tableRow: {
    borderBottom: `1px solid ${theme.colors.border}`,
    transition: 'background-color 0.2s'
  },
  tableRowHover: {
    backgroundColor: theme.colors.backgroundSecondary
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: theme.colors.text,
    borderBottom: `1px solid ${theme.colors.border}`
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  statusActive: {
    backgroundColor: theme.colors.success,
    color: '#ffffff'
  },
  statusOverdue: {
    backgroundColor: theme.colors.danger,
    color: '#ffffff'
  },
  statusCompleted: {
    backgroundColor: theme.colors.purple,
    color: '#ffffff'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  actionButton: {
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    border: `1px solid ${theme.colors.border}`,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  // Floating Action Button
  fab: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: theme.colors.blue,
    color: '#ffffff',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(31, 111, 235, 0.3)',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600'
  },
  fabHover: {
    transform: 'scale(1.05)',
    backgroundColor: theme.colors.primaryHover,
    boxShadow: '0 6px 20px rgba(31, 111, 235, 0.4)'
  },
  // Improved modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.2s ease'
  },
  modalCard: {
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    animation: 'slideUp 0.3s ease'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${theme.colors.border}`
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: theme.colors.textBright,
    margin: 0
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.textSecondary,
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.2s'
  },
  closeButtonHover: {
    backgroundColor: theme.colors.border,
    color: theme.colors.text
  },
  headerButton: {
    backgroundColor: theme.colors.blue,
    color: '#ffffff',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(31, 111, 235, 0.2)'
  },
  headerButtonHover: {
    backgroundColor: theme.colors.primaryHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(31, 111, 235, 0.3)'
  },
  // Loading skeleton styles
  skeleton: {
    backgroundColor: theme.colors.backgroundTertiary,
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonText: {
    height: '16px',
    marginBottom: '8px',
  },
  skeletonTitle: {
    height: '24px',
    width: '60%',
    marginBottom: '12px',
  },
  // Responsive breakpoints
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
  }
};

export default theme;
